// Estate-wide executor notes widget (§1.6, D-123.16; s124 Fix Round 1). A floating,
// expandable panel on the full-estate executor surface where the executor jots working
// notes while settling an estate. The list is the CONSOLIDATED view (aggregate-on-read):
// general notes (executor_notes) unioned with every task-drawer note (task_events note).
// Bottom-RIGHT, stacked ABOVE the feedback pill (sage-on-cream reads; sage-on-dark-sage
// sidebar did not — Fix 1). Compose writes general notes only; task notes are read-only
// here and deep-link to their drawer.
//
// Data: GET/POST /api/executor/:clientId/notes (executor-verified). The list uses the
// default queryFn (queryKey[0] = URL); the create mutation invalidates it on success.

import { useState, useRef, useEffect, useMemo } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { NotebookPen, X, Loader2, Search } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import type { ExecutorNote } from "@shared/executor-notes";
import { SAGE, WARM } from "@shared/brand";

// Brand tokens (brand-guidelines.md) — inlined so the floating panel is self-contained.

interface ExecutorNotesWidgetProps {
  clientId: string;
}

export function ExecutorNotesWidget({ clientId }: ExecutorNotesWidgetProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const notesKey = `/api/executor/${clientId}/notes`;

  const { data, isLoading, refetch } = useQuery<{ notes: ExecutorNote[] }>({
    queryKey: [notesKey],
    staleTime: 30_000,
    enabled: !!clientId,
  });
  const notes = data?.notes ?? [];

  // Opening the panel refetches so it never shows a stale list — belt-and-suspenders
  // alongside the drawer's cross-component invalidation (NOTES-FIX-2 A-0).
  const openPanel = () => { setOpen(true); refetch(); };

  // Fix 6 — client-side filter over the already-fetched consolidated list. Case-insensitive
  // substring match on note text AND the source task title (so "bank" finds a general note
  // mentioning banks and any note made on the Notify-banks task).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter(
      (n) => n.text.toLowerCase().includes(q) || (n.source?.taskTitle ?? "").toLowerCase().includes(q),
    );
  }, [notes, search]);

  const createNote = useMutation({
    mutationFn: async (text: string) =>
      (await apiRequest("POST", notesKey, { text })).json(),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: [notesKey] });
    },
  });

  // Auto-expand the compose textarea (2 rows → ~5 rows max) as the executor types.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [draft]);

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): the `createNote.isPending` check below was a READ of React
  // state, which lags a render and so cannot stop two submits inside one tick. Kept as the cheap
  // early-out; the lock is what actually holds.
  const noteFlight = useSingleFlight();

  const submit = () => {
    const text = draft.trim();
    if (!text || createNote.isPending) return;
    noteFlight.runQuiet(() => createNote.mutateAsync(text));
  };

  // Fix 5 — deep-link a task-sourced note to its drawer. Close the panel so the task list
  // is visible, then navigate with ?task=<taskKey> (ExecutorTaskList auto-opens + scrolls).
  const openTask = (taskKey: string) => {
    setOpen(false);
    setLocation(`/executor/${clientId}/tasks?task=${encodeURIComponent(taskKey)}`);
  };

  if (!clientId) return null;

  // ── Collapsed: a circular launcher stacked above the feedback pill (Fix 1) ─────
  if (!open) {
    return (
      <button
        type="button"
        onClick={openPanel}
        aria-label="Open estate notes"
        data-testid="executor-notes-launcher"
        className="fixed bottom-20 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105"
        style={{ backgroundColor: SAGE[600] }}
      >
        <NotebookPen className="h-5 w-5" />
        {notes.length > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold text-white ring-2"
            style={{ backgroundColor: SAGE[600], ["--tw-ring-color" as any]: WARM.white }}
            data-testid="executor-notes-count"
          >
            {notes.length}
          </span>
        )}
      </button>
    );
  }

  // ── Expanded: compose + search + consolidated list (Fixes 2/3/4/5/6) ───────────
  // Anchored bottom-right; may overlay the feedback pill when open (acceptable — Fix 1).
  return (
    <div
      className="fixed bottom-5 right-5 z-[60] flex w-[420px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl shadow-lg"
      style={{ maxHeight: "70vh", backgroundColor: WARM.white, border: `1px solid ${WARM.stone}`, fontFamily: '"DM Sans", sans-serif' }}
      data-testid="executor-notes-panel"
    >
      {/* Header (pinned) */}
      <div className="flex shrink-0 items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${WARM.stone}` }}>
        <span className="text-sm font-semibold" style={{ color: WARM.charcoal }}>Notes</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close estate notes"
          data-testid="executor-notes-close"
          className="rounded p-0.5 transition-colors hover:opacity-70"
          style={{ color: WARM.taupe }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Compose (pinned) */}
      <div className="shrink-0 px-4 pt-3 pb-2" style={{ borderBottom: `1px solid ${WARM.stone}` }}>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); }
          }}
          rows={2}
          placeholder="Add a note…"
          data-testid="executor-notes-input"
          className="w-full resize-none rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal, ["--tw-ring-color" as any]: SAGE[600] }}
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || createNote.isPending}
            data-testid="executor-notes-add"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: SAGE[600] }}
          >
            {createNote.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add note
          </button>
        </div>
      </div>

      {/* Search (pinned) — only once there is something to filter (Fix 6) */}
      {notes.length > 0 && (
        <div className="shrink-0 px-4 py-2" style={{ borderBottom: `1px solid ${WARM.stone}` }}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" style={{ color: WARM.taupe }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search notes…"
              data-testid="executor-notes-search"
              className="w-full rounded-md py-1.5 pl-8 pr-8 text-sm focus:outline-none focus:ring-2"
              style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal, ["--tw-ring-color" as any]: SAGE[600] }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                data-testid="executor-notes-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 transition-colors hover:opacity-70"
                style={{ color: WARM.taupe }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* List (scrollable — Fix 2) */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="executor-notes-list">
        {isLoading ? (
          <p className="text-sm" style={{ color: WARM.taupe }}>Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-[15px] leading-relaxed" style={{ color: WARM.taupe }} data-testid="executor-notes-empty">
            No notes yet. Use this space to track calls, decisions, and reminders as you settle the estate.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-[15px] leading-relaxed" style={{ color: WARM.taupe }} data-testid="executor-notes-no-match">
            No notes match — clear the search to see all notes.
          </p>
        ) : (
          <ul className="space-y-3.5">
            {filtered.map((note) => (
              <li key={note.id} data-testid="executor-note-item">
                <p className="whitespace-pre-wrap break-words text-[15px] leading-snug" style={{ color: WARM.charcoal }}>
                  {note.text}
                </p>
                {note.source && (
                  <button
                    type="button"
                    onClick={() => openTask(note.source!.taskKey)}
                    data-testid="executor-note-source"
                    className="mt-0.5 block max-w-full truncate text-left text-xs underline decoration-dotted underline-offset-2 transition-opacity hover:opacity-70"
                    style={{ color: WARM.taupe }}
                    title={`Open task: ${note.source.taskTitle}`}
                  >
                    re: {note.source.taskTitle}
                  </button>
                )}
                <p className="mt-0.5 text-xs" style={{ color: WARM.taupe }}>
                  — {note.createdByName} · {formatStamp(note.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Fix 4 — absolute stamp: "Jul 7, 3:42 PM"; include the year only for a prior year
// ("Dec 3, 2025, 1:10 PM") since settlements run 18+ months. Graceful fallback on a bad date.
function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return format(d, sameYear ? "MMM d, h:mm a" : "MMM d, yyyy, h:mm a");
}
